import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Shift } from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Input, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RemoveButton } from "../components/ui/RemoveButton";
import { can } from "../lib/permissions";
import { useEffectiveUser } from "../lib/auth";
import { NEON_SHIFT_COLORS } from "../lib/colors";

const empty: Partial<Shift> = {
  name: "",
  startTime: "06:00",
  durationMin: 10 * 60,
  color: "#1e6bff",
  minStaffing: 2,
  slotCount: 8,
  sortOrder: 0,
  requiresOic: true,
  tacRole: "regular",
  coverageInactiveWeekdays: [],
};

export function Shifts() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const mayEdit = can.editShifts(me);
  const mayDelete = can.deleteShifts(me);
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const archived = useQuery({
    queryKey: ["shifts-archived"],
    queryFn: api.listArchivedShifts,
    enabled: mayEdit,
  });
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const [editing, setEditing] = useState<Partial<Shift> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [armedArchiveId, setArmedArchiveId] = useState<string | null>(null);
  // Two-step permanent delete for an archived shift. When the
  // user clicks "Delete permanently" we open this dialog with the
  // count of assignment rows that will go away in the cascade —
  // they have to confirm explicitly before the delete fires.
  const [deleteTarget, setDeleteTarget] = useState<Shift | null>(null);

  const saveMut = useMutation({
    mutationFn: async (s: Partial<Shift>) => {
      if (s.id) return api.updateShift(s.id, s);
      return api.createShift(s);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      // Coverage rolls up min-staffing + requires_oic per shift, so
      // toggling those on the edit modal needs a fresh coverage
      // compute too — otherwise the Schedule page keeps the OIC
      // row + red chips from the prior settings until the next
      // navigation.
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setEditing(null);
      setError(null);
    },
    onError: (err: { message?: string }) => setError(err?.message ?? "Save failed"),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => api.archiveShift(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["shifts-archived"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setArmedArchiveId(null);
      setError(null);
    },
    onError: (e: { message?: string }) => {
      setArmedArchiveId(null);
      setError(e?.message ?? "Archive failed");
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: (id: string) => api.unarchiveShift(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["shifts-archived"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Unarchive failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShift(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["shifts-archived"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      setDeleteTarget(null);
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Delete failed"),
  });

  return (
    <div className="p-4 md:p-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Shifts</h1>
          <p className="text-sm text-slate-500">
            {shifts.data?.length ?? 0} active. Add or remove as your agency needs.
          </p>
        </div>
        {mayEdit && (
          <Button
            onClick={() => setEditing({
              ...empty,
              sortOrder: (shifts.data?.length ?? 0),
            })}
          >
            + New shift
          </Button>
        )}
      </header>

      {error && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-700 font-semibold leading-none"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium w-8"></th>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Department</th>
              <th className="px-3 py-2 font-medium">Start</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              <th className="px-3 py-2 font-medium">Min staffing</th>
              <th className="px-3 py-2 font-medium">Slots</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(shifts.data ?? []).map((s) => {
              const dept = departments.data?.find((d) => d.id === s.departmentId);
              return (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <span className="inline-block w-3 h-3 rounded" style={{ background: s.color }} />
                </td>
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2 text-slate-600">{dept?.name ?? "—"}</td>
                <td className="px-3 py-2">{s.startTime}</td>
                <td className="px-3 py-2">{Math.round(s.durationMin / 60 * 10) / 10}h</td>
                <td className="px-3 py-2">{s.minStaffing}</td>
                <td className="px-3 py-2">{s.slotCount ?? 8}</td>
                <td className="px-3 py-2 text-right">
                  {mayEdit && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>Edit</Button>
                      <RemoveButton
                        armed={armedArchiveId === s.id}
                        onArm={() => setArmedArchiveId(s.id)}
                        onConfirm={() => archiveMut.mutate(s.id)}
                        label="Archive"
                        confirmLabel={`Confirm archive ${s.name}`}
                        title="Existing assignments are preserved; the shift disappears from new schedules."
                      />
                    </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {mayEdit && (archived.data?.length ?? 0) > 0 && (
        <ArchivedShiftsSection
          archived={archived.data ?? []}
          departments={departments.data ?? []}
          mayDelete={mayDelete}
          onUnarchive={(id) => unarchiveMut.mutate(id)}
          onAskDelete={(s) => setDeleteTarget(s)}
          unarchiveBusy={unarchiveMut.isPending}
        />
      )}

      {deleteTarget && (
        <DeleteShiftModal
          target={deleteTarget}
          onClose={() => { setDeleteTarget(null); setError(null); }}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
          busy={deleteMut.isPending}
        />
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit shift" : "New shift"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && saveMut.mutate(editing)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Department">
              <select
                value={editing.departmentId ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, departmentId: e.target.value })
                }
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="" disabled>— Pick —</option>
                {(departments.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Name">
              <Input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </Field>
            <Field label="Color">
              <ShiftColorPicker
                value={editing.color ?? "#1e6bff"}
                onChange={(c) => setEditing({ ...editing, color: c })}
              />
            </Field>
            <Field label="Start time">
              <Input
                type="time"
                value={editing.startTime ?? "06:00"}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setEditing({
                    ...editing,
                    startTime: newStart,
                    // Keep durationMin in sync with (end - start) so
                    // changing the start time shifts the window
                    // rather than resizing it. End stays put.
                    durationMin: deriveDuration(newStart, endTimeOf(editing)),
                  });
                }}
              />
            </Field>
            <Field label="End time">
              <Input
                type="time"
                value={endTimeOf(editing)}
                onChange={(e) => {
                  const newEnd = e.target.value;
                  setEditing({
                    ...editing,
                    durationMin: deriveDuration(editing.startTime ?? "06:00", newEnd),
                  });
                }}
              />
            </Field>
            <Field label="Duration">
              <div className="px-3 py-1.5 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded">
                {formatDuration(editing.durationMin ?? 0)}
                {endTimeIsOvernight(editing.startTime ?? "06:00", endTimeOf(editing)) && (
                  <span className="ml-2 text-xs text-slate-500">(overnight)</span>
                )}
              </div>
            </Field>
            <Field label="Min staffing">
              <Input
                type="number"
                min={0}
                value={editing.minStaffing ?? 1}
                onChange={(e) =>
                  setEditing({ ...editing, minStaffing: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Slots (roster spots)">
              <Input
                type="number"
                min={1}
                max={50}
                value={editing.slotCount ?? 8}
                onChange={(e) =>
                  setEditing({ ...editing, slotCount: Number(e.target.value) })
                }
              />
            </Field>
            <Field label="Sort order">
              <Input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) =>
                  setEditing({ ...editing, sortOrder: Number(e.target.value) })
                }
              />
            </Field>
            <label className="col-span-2 flex items-start gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={editing.inRotation ?? true}
                onChange={(e) =>
                  setEditing({ ...editing, inRotation: e.target.checked })
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium">Include in coverage rotation</span>
                <span className="block text-xs text-slate-500">
                  Turn off for specialty roles like Traffic, SRO, or K-9
                  that don't cover — or get covered by — the 24/7 shifts.
                </span>
              </span>
            </label>
            <label className="col-span-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.requiresOic ?? true}
                onChange={(e) =>
                  setEditing({ ...editing, requiresOic: e.target.checked })
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium">Require an OIC on duty</span>
                <span className="block text-xs text-slate-500">
                  Cells turn red when no Officer-in-Charge is assigned. Turn
                  off for shifts / departments that don't run an OIC seat —
                  Dispatch, Animal Control, etc.
                </span>
              </span>
            </label>
            <label className="col-span-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.presenceCoverage ?? false}
                onChange={(e) =>
                  setEditing({ ...editing, presenceCoverage: e.target.checked })
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="font-medium">Count coverage per day, not per hour</span>
                <span className="block text-xs text-slate-500">
                  Turn on for shifts where a single officer working their
                  own hours fully covers the day — Animal Control with two
                  ACOs on staggered 8-4 / 9-5 schedules, for example.
                  The shift reads as covered whenever at least min-
                  staffing officers have a working assignment that date,
                  regardless of whether the fringe hours of the shift
                  window are technically staffed.
                </span>
              </span>
            </label>
            <div className="col-span-2">
              <Field label="12-hour Shift behavior">
                <Select
                  value={editing.tacRole ?? "regular"}
                  onChange={(e) =>
                    setEditing({ ...editing, tacRole: e.target.value as Shift["tacRole"] })
                  }
                >
                  <option value="regular">Regular — hidden when 12-hour Shifts are active</option>
                  <option value="tac">12-hour shift — shown only when 12-hour Shifts are active</option>
                  <option value="exempt">Exempt — always shown (Dispatch, Animal Control…)</option>
                </Select>
              </Field>
              <p className="mt-1 text-xs text-slate-500">
                Controls whether this shift appears on the schedule grid
                while the agency is running 12-hour Shifts.
              </p>
            </div>
            <div className="col-span-2 pt-3 mt-1 border-t border-slate-200">
              <CoverageInactiveWeekdaysEditor
                value={editing.coverageInactiveWeekdays ?? []}
                onChange={(v) => setEditing({ ...editing, coverageInactiveWeekdays: v })}
              />
            </div>
            <div className="col-span-2 pt-3 mt-1 border-t border-slate-200">
              <OvertimeWindowsEditor
                windows={editing.overtimeWindows ?? []}
                onChange={(w) => setEditing({ ...editing, overtimeWindows: w })}
              />
            </div>
            {error && <div className="col-span-2 text-sm text-red-600">{error}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ----------------------------------------------------------------------
// Time helpers for the start+end time inputs. durationMin stays the
// source of truth on the Shift row; we derive it from start/end so
// supervisors don't have to do mental math. Overnight shifts (end
// before start in wall-clock) wrap to next day.

function parseHHMM(s: string | undefined): number {
  if (!s) return 0;
  const [h, m] = s.split(":").map((n) => Number(n));
  return (h || 0) * 60 + (m || 0);
}

function endTimeOf(edit: Partial<Shift>): string {
  const start = parseHHMM(edit.startTime ?? "06:00");
  const dur = edit.durationMin ?? 0;
  const end = (start + dur) % (24 * 60);
  const h = Math.floor(end / 60);
  const m = end % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function deriveDuration(start: string, end: string): number {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  const diff = e - s;
  // A 0-minute diff is treated as a full 24-hour shift rather than
  // a zero-length one (which isn't a real shift).
  if (diff === 0) return 24 * 60;
  return diff > 0 ? diff : diff + 24 * 60;
}

function endTimeIsOvernight(start: string, end: string): boolean {
  return parseHHMM(end) <= parseHHMM(start);
}

function formatDuration(mins: number): string {
  if (mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// ----------------------------------------------------------------------
// Overtime-window list editor — lives inside the Shifts edit modal.
// Each row is a preset start/end pair the supervisor can pick from
// when an officer from this shift is assigned to OT coverage (in the
// Fix-coverage modal). An empty list means "Custom hours only".

function OvertimeWindowsEditor({
  windows, onChange,
}: {
  windows: { label: string; startTime: string; endTime: string }[];
  onChange: (next: { label: string; startTime: string; endTime: string }[]) => void;
}) {
  function update(idx: number, patch: Partial<typeof windows[number]>) {
    const next = windows.slice();
    next[idx] = { ...next[idx], ...patch };
    // Re-derive the label if the user didn't override it or the
    // label still matches the old auto-label.
    const w = next[idx];
    const auto = autoLabel(w.startTime, w.endTime);
    if (!w.label || w.label === autoLabel(windows[idx].startTime, windows[idx].endTime)) {
      next[idx] = { ...w, label: auto };
    }
    onChange(next);
  }
  function add() {
    onChange([
      ...windows,
      { label: "6-18", startTime: "06:00", endTime: "18:00" },
    ]);
  }
  function remove(idx: number) {
    onChange(windows.filter((_, i) => i !== idx));
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-slate-900">Overtime windows</div>
          <div className="text-xs text-slate-500">
            Preset worked-hours options offered when an officer from this
            shift picks up overtime (e.g. Day: 2–14, 6–18). Leave empty
            to require a custom hours entry every time.
          </div>
        </div>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add window
        </button>
      </div>
      {windows.length === 0 ? (
        <p className="text-xs italic text-slate-400">
          No windows configured. Supervisors will be prompted for custom
          hours each time they assign OT from this shift.
        </p>
      ) : (
        <div className="space-y-1.5">
          {windows.map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="time"
                value={w.startTime}
                onChange={(e) => update(i, { startTime: e.target.value })}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              />
              <span className="text-slate-400">→</span>
              <input
                type="time"
                value={w.endTime}
                onChange={(e) => update(i, { endTime: e.target.value })}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              />
              <input
                type="text"
                value={w.label}
                placeholder="Label"
                onChange={(e) => onChange(windows.map((ww, ii) => ii === i ? { ...ww, label: e.target.value } : ww))}
                className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-slate-400 hover:text-red-600 text-sm"
                title="Remove this window"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Archived shifts — restore or delete permanently.
//
// Archive (the soft delete) preserves the row and every assignment
// that ever pointed at it; unarchive (set archived_at = null)
// brings it all back unchanged. Permanent delete cascades through
// the assignments FK and wipes schedule history, so it's gated
// behind a count-and-confirm dialog AND restricted to Owner / L1.

function ArchivedShiftsSection({
  archived, departments, mayDelete, onUnarchive, onAskDelete,
  unarchiveBusy,
}: {
  archived: Shift[];
  departments: { id: string; name: string }[];
  mayDelete: boolean;
  onUnarchive: (id: string) => void;
  onAskDelete: (s: Shift) => void;
  unarchiveBusy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const deptById = new Map(departments.map((d) => [d.id, d.name]));
  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1.5"
      >
        <span className="inline-block w-3 text-center">{open ? "▾" : "▸"}</span>
        Archived shifts ({archived.length})
      </button>
      {open && (
        <div className="mt-2 bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2 font-medium w-8"></th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Department</th>
                <th className="px-3 py-2 font-medium">Archived</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {archived.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 text-slate-600">
                  <td className="px-3 py-2">
                    <span className="inline-block w-3 h-3 rounded opacity-50"
                      style={{ background: s.color }} />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-700">{s.name}</td>
                  <td className="px-3 py-2">
                    {deptById.get(s.departmentId) ?? "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {s.archivedAt
                      ? new Date(s.archivedAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={unarchiveBusy}
                      onClick={() => onUnarchive(s.id)}
                      title="Restore this shift — its assignments come back exactly as they were."
                    >
                      Restore
                    </Button>
                    {mayDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onAskDelete(s)}
                        title="Permanently delete this shift and every assignment that ever referenced it."
                      >
                        Delete permanently
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeleteShiftModal({
  target, onClose, onConfirm, busy,
}: {
  target: Shift;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  // Show the user how much history they'll lose before they
  // commit. The count is the number of assignment rows that
  // reference this shift; with ON DELETE CASCADE on the FK they
  // all go away in the same transaction.
  const count = useQuery({
    queryKey: ["shift-assignments-count", target.id],
    queryFn: () => api.countShiftAssignments(target.id),
  });
  const phrase = `delete ${target.name}`;
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === phrase.toLowerCase();
  return (
    <Modal
      open={true}
      onClose={() => { if (!busy) onClose(); }}
      title={`Permanently delete "${target.name}"?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!matches || busy}
            onClick={onConfirm}
          >
            {busy ? "Deleting…" : "Permanently delete"}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-900">
          This cannot be undone. The shift row and{" "}
          <strong>
            {count.isLoading
              ? "(counting…)"
              : `${count.data ?? 0} assignment${count.data === 1 ? "" : "s"}`}
          </strong>{" "}
          that reference it will be deleted from the database. The
          Schedule's history for this shift will disappear.
        </div>
        <p className="text-slate-700">
          If you just need it out of the active roster, click Cancel
          and Restore it later instead — Archive keeps every row
          intact and reversible.
        </p>
        <Field label={`Type "${phrase}" to confirm`}>
          <Input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={phrase}
          />
        </Field>
      </div>
    </Modal>
  );
}

function autoLabel(start: string, end: string): string {
  const s = Number(start.split(":")[0]);
  const e = Number(end.split(":")[0]);
  return `${s}-${e}`;
}

// Grid of neon swatches + a small fallback to the native color
// input for agencies that want to match their patch / decal hex
// exactly. The current value lights up with a darker ring; an
// off-palette custom color shows as a 13th swatch on the right
// so the supervisor can see the active hex without re-opening
// the picker.
function ShiftColorPicker({
  value, onChange,
}: { value: string; onChange: (hex: string) => void }) {
  const lowerValue = value.toLowerCase();
  const inPalette = NEON_SHIFT_COLORS.some((c) => c.hex.toLowerCase() === lowerValue);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {NEON_SHIFT_COLORS.map((c) => {
          const selected = c.hex.toLowerCase() === lowerValue;
          return (
            <button
              key={c.hex}
              type="button"
              title={`${c.name} (${c.hex})`}
              aria-label={c.name}
              onClick={() => onChange(c.hex)}
              className={
                "w-7 h-7 rounded-md border-2 transition shadow-sm "
                + (selected
                  ? "border-slate-900 ring-2 ring-offset-1 ring-slate-400"
                  : "border-white hover:border-slate-300")
              }
              style={{ background: c.hex }}
            />
          );
        })}
        {!inPalette && value && (
          <div
            title={`Custom (${value})`}
            className="w-7 h-7 rounded-md border-2 border-slate-900 ring-2 ring-offset-1 ring-slate-400 shadow-sm"
            style={{ background: value }}
          />
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0"
          aria-label="Custom color"
        />
        <span className="text-xs font-mono text-slate-500">{value.toUpperCase()}</span>
      </div>
    </div>
  );
}

// Weekday checkboxes for "don't flag this day as needing coverage."
// Built for SRO covering school hours (Sat/Sun off) and similar
// schedules where a 0/N on a given weekday isn't actually a gap.
function CoverageInactiveWeekdaysEditor({
  value, onChange,
}: { value: number[]; onChange: (next: number[]) => void }) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const set = new Set(value);
  function toggle(d: number) {
    const next = new Set(set);
    if (next.has(d)) next.delete(d); else next.add(d);
    onChange(Array.from(next).sort());
  }
  return (
    <div>
      <div className="text-sm font-medium text-slate-900">Skip coverage on</div>
      <p className="text-xs text-slate-500 mt-0.5">
        Weekdays this shift doesn't need to be staffed. Coverage and
        OIC cells on these days render as N/A instead of red short —
        useful for the School Resource Officer on weekends, training
        shifts that only run Mon–Fri, etc. Leave blank for a shift
        that runs every day.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {days.map((label, i) => {
          const on = set.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              className={
                "px-2.5 py-1 rounded border text-xs font-semibold uppercase tracking-wider transition " +
                (on
                  ? "bg-slate-800 border-slate-800 text-white"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50")
              }
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
